import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignMessageEditor } from '#/entities/campaign'
import { sanitizeSmsText } from '#/shared/lib/sms-sanitize'
import { computeSmsSegments } from '#/shared/lib/sms-segments'
import { Select } from '#/shared/ui/select'

import type { UseCampaignComposerResult } from '../model/use-campaign-composer'
import { CampaignComposerControls } from './campaign-composer-controls'

interface CampaignSmsComposerProps {
  composer: UseCampaignComposerResult
}

/** The `SENDS FROM` / `PREVIEW` column beside the SMS editor. */
function SmsSidebar({ composer }: CampaignSmsComposerProps) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full shrink-0 flex-col gap-2 lg:w-60">
      <div className="flex flex-col gap-2 rounded-[10px] bg-paper-avatar p-3">
        <p className="text-2xs font-medium text-warm-500">
          {t('campaigns.composer.sendsFrom')}
        </p>
        <Select
          selectSize="sm"
          options={composer.lineOptions}
          placeholder={t('campaigns.composer.noLine')}
          value={composer.fromNumber}
          onValueChange={composer.setFromNumber}
          className="rounded-full border-transparent bg-white"
        />
      </div>
      <div className="flex flex-col gap-2 rounded-[10px] bg-paper-avatar p-3">
        <p className="text-2xs font-medium text-warm-500">
          {t('campaigns.composer.preview')}
        </p>
        {/* Merge fields are shown as written: resolving them for a named
            person is a dry run the service only offers for a saved step. */}
        <p className="rounded-xl bg-white px-3 py-2.5 text-sm leading-5 whitespace-pre-line text-announce-ink">
          {composer.body}
        </p>
      </div>
    </div>
  )
}

export function CampaignSmsComposer({ composer }: CampaignSmsComposerProps) {
  const { t } = useTranslation()
  // The textarea holds the body verbatim, so only the send path's character
  // clean stands between it and the wire — an em dash here is folded, not a
  // reason to price the message as UCS-2.
  const info = useMemo(
    () => computeSmsSegments(sanitizeSmsText(composer.body)),
    [composer.body],
  )

  return (
    <div className="flex flex-col gap-4 p-3.5 lg:flex-row">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Select
          selectSize="sm"
          options={composer.templateOptions}
          placeholder={t('campaigns.composer.templatePlaceholder')}
          value={composer.template}
          onValueChange={composer.setTemplate}
          className="w-fit rounded-full bg-gray-100"
        />
        <div className="rounded-lg border border-gray-200">
          <CampaignMessageEditor
            value={composer.body}
            onChange={composer.setBody}
            label={t('campaigns.composer.messageLabel')}
            className="min-h-27.5"
          />
        </div>
        <p className="text-xs text-warm-500">
          {t('campaigns.composer.smsCounter', {
            chars: info.charCount,
            count: info.segments,
            encoding: info.encoding,
          })}
        </p>
        <p className="text-xs text-warm-500">
          {t('campaigns.composer.stopNotice')}
        </p>
        <CampaignComposerControls composer={composer} />
      </div>
      <SmsSidebar composer={composer} />
    </div>
  )
}
